import { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import moment from "jalali-moment";
import SiteTitle from "../../../utils/SiteTitle/SiteTitle";
import CardLayout from "../../../utils/CardLayout/CardLayout";
import EmptyData from "../../../utils/EmptyData/EmptyData";
import PaginationList from "../../../utils/PaginationList/PaginationList";
import { getSMSDispatchLogsService } from "../../../services/userServices";

const formatDate = (value) => value ? moment(value).locale("fa").format("YYYY/MM/DD HH:mm:ss") : "—";

export const SMSDispatchLogList = ({ orderId, compact = false }) => {
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const loadLogs = async () => {
      if (compact && orderId != null) {
        const items = [];
        let hasMore = true;
        while (active && hasMore) {
          const { data } = await getSMSDispatchLogsService({
            order_id: orderId, limit: 100, offset: items.length,
          });
          items.push(...data.results);
          hasMore = Boolean(data.next) && data.results.length > 0;
        }
        return { data: { results: items, count: items.length } };
      }
      return getSMSDispatchLogsService({
      limit, offset: (page - 1) * limit, search,
      delivery_status: deliveryStatus,
      ...(orderId != null && { order_id: orderId }),
      });
    };
    loadLogs().then(({ data }) => {
      if (active) {
        setLogs(data.results);
        setCount(data.count);
      }
    }).catch(() => {
      if (active) setError("دریافت پیامک های ارسالی ناموفق بود. دوباره تلاش کنید.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [page, limit, search, deliveryStatus, refresh, orderId, compact]);

  return (
    <>
        <h4>پیامک های ارسالی</h4>
        {!compact && <form className="d-flex flex-wrap align-items-end mb-3" onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
          setRefresh((value) => value + 1);
        }}>
          <div className="mr-2 mb-2">
            <label htmlFor="sms-log-search">جستجوی گیرنده، متن یا شناسه پیامک</label>
            <input id="sms-log-search" className="form-control" value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)} />
          </div>
          <div className="mr-2 mb-2">
            <label htmlFor="sms-log-status">وضعیت ارسال</label>
            <select id="sms-log-status" className="form-control" value={deliveryStatus}
              onChange={(event) => { setPage(1); setDeliveryStatus(event.target.value); }}>
              <option value="">همه</option>
              <option value="delivered">رسیده به گوشی</option>
              <option value="failed">ناموفق</option>
              <option value="pending">در انتظار نتیجه</option>
            </select>
          </div>
          <button className="btn btn-primary mr-2 mb-2" type="submit">جستجو</button>
          <button className="btn btn-secondary mb-2" type="button"
            onClick={() => setRefresh((value) => value + 1)}>بروزرسانی</button>
        </form>}
        {loading ? <p role="status">در حال دریافت پیامک ها...</p> : error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span>تعداد: {count}</span>
              {!compact && <div>
                <label htmlFor="sms-log-limit" className="mr-2">تعداد در صفحه</label>
                <select id="sms-log-limit" value={limit} onChange={(event) => {
                  setPage(1); setLimit(Number(event.target.value));
                }}>
                  {[10, 25, 50, 100].map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </div>}
            </div>
            {logs.length === 0 ? <EmptyData /> : (
              <Table responsive hover striped>
                <thead className="thead-dark">
                  <tr>
                    <th>شناسه</th><th>زمان ارسال</th><th>گیرنده</th><th>نوع گیرنده</th>
                    <th>سفارش</th><th>متن پیامک</th><th>وضعیت</th><th>شناسه پیامک ترز</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td className="text-nowrap" dir="ltr">{formatDate(log.created)}</td>
                      <td><div>{log.recipient_name || "—"}</div><div dir="ltr">{log.recipient_mobile}</div></td>
                      <td>{({ personnel: "پرسنل", client: "مشتری", user: "کاربر", admin: "مدیر" })[log.recipient_type] || log.recipient_type}</td>
                      <td>{log.order || "—"}</td>
                      <td style={{ minWidth: 250, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{log.message}</td>
                      <td>
                        <span className={`badge ${log.is_failed ? "bg-danger" : log.is_delivered ? "bg-success" : "bg-secondary"}`}>
                          {log.is_failed ? "ناموفق" : log.is_delivered ? "رسیده به گوشی" : "در انتظار نتیجه"}
                        </span>
                        <div>{log.status_text}</div>
                        {log.status_code && <div>کد وضعیت: {log.status_code}</div>}
                        {log.alert_sent && <div>هشدار ارسال شده</div>}
                      </td>
                      <td>{log.message_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {!compact && <PaginationList initialPage={page - 1} pageCount={Math.ceil(count / limit)} setPageNumber={setPage} />}
          </>
        )}
    </>
  );
};

const SMSDispatchLogAdmin = () => (
  <>
    <SiteTitle title="پیامک های ارسالی" />
    <CardLayout>
      <SMSDispatchLogList />
    </CardLayout>
  </>
);

export default SMSDispatchLogAdmin;
